import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { DialogPosition, MAT_DIALOG_DATA, MAT_DIALOG_DEFAULT_OPTIONS, MatDialogRef } from '@angular/material/dialog';
import { CanDestroyNotify, CanDestroyNotifyCtor, mixinDestroyNotifier, MtnaErrorService, MtnaLogger } from '@mtna/core-angular';
import { SnippetGenerator } from '@rds/rds-packaging-ts';
import { RdsApiSnippetQueryType, RdsRawApiQuery, SelectParameters, TabulationParameters } from '@rds/shared-api';
import { BehaviorSubject, iif, Observable } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { RdsSnippetService } from '../core/services/rds-snippet.service';

/** Horizontal alignment position */
export type RdsApiSnippetDialogPositionX = 'after' | 'before';
/** Vertical alignment position */
export type RdsApiSnippetDialogPositionY = 'above' | 'below';

/**
 * Data to inject to the RdsApiSnippetsDialogComponent
 */
export class RdsApiSnippetsDialogData {
  /**
   * @param queryType the type of query for which to generate snippets
   * @param params the query parameters
   * @param relativeTo optional html element relative to which to position the dialog, if not provided the dialog will be centered
   * @param positionX horizontal position alignment, defaults to 'before'
   * @param positionY vertical position alignment, defaults to 'below'
   */
  constructor(
    public queryType: RdsApiSnippetQueryType,
    public params: SelectParameters | TabulationParameters,
    public relativeTo?: HTMLElement,
    public positionX: RdsApiSnippetDialogPositionX = 'before',
    public positionY: RdsApiSnippetDialogPositionY = 'below'
  ) {}
}

// Boilerplate for applying mixins to RdsApiSnippetsDialogComponent.
class RdsApiSnippetsDialogComponentBase {}

const _RdsApiSnippetsDialogComponentMixinBase: CanDestroyNotifyCtor = mixinDestroyNotifier(RdsApiSnippetsDialogComponentBase);

/**
 * Dialog to display RDS API code snippets.
 * Makes use of the RDS Snippets Generator API.
 * @author Will Davis <will.davis@mtna.us>
 */
@Component({
  selector: 'rds-c-api-snippets-dialog',
  templateUrl: './api-snippets-dialog.component.html',
  styleUrls: ['./api-snippets-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { hasBackdrop: true, minWidth: '500px', minHeight: '400px' },
    },
  ],
})
export class RdsApiSnippetsDialogComponent extends _RdsApiSnippetsDialogComponentMixinBase implements CanDestroyNotify, OnInit {
  generators$ = new BehaviorSubject<SnippetGenerator[] | null>(null);

  protected _rawApiQuery = new BehaviorSubject<RdsRawApiQuery | undefined>(undefined);
  /** Information about the API Query. The raw string and an html version for display */
  rawApiQuery$: Observable<RdsRawApiQuery | undefined> = this._rawApiQuery.asObservable();

  /**
   * Object to hold the loading status of each
   * library and version combination.
   * Key is the library name plus version, value is the loading status.
   */
  loading: { [libraryAndVersion: string]: boolean } = {};
  /**
   * Object to hold the lines of code for each
   * library and version combination.
   * Key is the library name plus version, value is the lines of code.
   */
  loc: { [libraryAndVersion: string]: string[] } = {};
  /**
   * Object to hold the selected version for each library.
   * Key is the library name, value is the version.
   */
  selected: { [library: string]: string } = {};

  noDescription = $localize`No description available`;

  constructor(
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: RdsApiSnippetsDialogData,
    public dialogRef: MatDialogRef<RdsApiSnippetsDialogComponent>,
    private errorService: MtnaErrorService,
    private logger: MtnaLogger,
    private rdsSnippetService: RdsSnippetService
  ) {
    super();
    // Add a container class
    this.dialogRef.addPanelClass('rds-c-api-snippets-dialog-container');

    // Log error if required input properties are not provided
    if (!this.data?.queryType || !this.data?.params) {
      this.logger.error(
        `[RdsApiSnippetsDialogComponent] queryType and params are required.\nqueryType:`,
        this.data?.queryType,
        `\nparams:`,
        this.data?.params
      );
    } else {
      // Initialize raw query information
      this.rdsSnippetService
        .generateRawApiQuery(this.data.queryType, this.data.params)
        .pipe(take(1), takeUntil(this.destroyed$))
        .subscribe((query) => this._rawApiQuery.next(query));

      // Query the available generators
      this.rdsSnippetService
        .getSnippetGenerators()
        .pipe(take(1), takeUntil(this.destroyed$))
        .subscribe(
          (generators) => this.generators$.next(generators),
          (err) => {
            this.errorService.showError(err);
            this.dialogRef.close();
          }
        );
    }
  }

  handleTabChange(index: number, generators: SnippetGenerator[]) {
    if (index === 0) {
      // Do nothing for api tab
      return;
    }

    // If a generator tab is selected and no version is selected,
    // initially select the newest version (first in the array of supportedVersions)
    const selectedGenerator = generators[index - 1];
    if (!this.selected[selectedGenerator.library]) {
      this.selectVersion(selectedGenerator, selectedGenerator.supportedVersions[0]);
    }
  }

  ngOnInit() {
    // If a relative element has been provided,
    // update the dialog position
    if (this.data?.relativeTo) {
      const rect: DOMRect = this.data.relativeTo.getBoundingClientRect();
      const position: DialogPosition = {};

      // Horizontal position
      if (this.data.positionX === 'after') {
        position.left = `${rect.left}px`;
      } else {
        position.right = `calc(100vw - ${rect.right}px)`;
      }

      // Vertical position
      if (this.data.positionY === 'above') {
        position.bottom = `calc(100vh - ${rect.top}px)`;
      } else {
        position.top = `${rect.bottom}px`;
      }

      this.dialogRef.updatePosition(position);
    }
  }

  selectVersion(generator: SnippetGenerator, version: string) {
    this.selected[generator.library] = version;
    this.loading[generator.library + version] = true;
    this.cdr.markForCheck();

    // Query snippet
    iif(
      () => this.data.queryType === 'SELECT',
      this.rdsSnippetService.generateSelectSnippet(this.data.params, generator.library, version),
      this.rdsSnippetService.generateTabulateSnippet(this.data.params as TabulationParameters, generator.library, version)
    )
      .pipe(take(1), takeUntil(this.destroyed$))
      .subscribe(
        (snippet) => {
          this.loc[generator.library + version] = snippet.loc;
        },
        () => {
          delete this.loc[generator.library + version];
        },
        () => {
          this.loading[generator.library + version] = false;
          this.cdr.markForCheck();
        }
      );
  }
}
